import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable */
import { createContext, useContext, useEffect, useState } from 'react';
const AuthContext = createContext(undefined);
// Add this function at the top level
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
        return parts.pop()?.split(';').shift() || null;
    return null;
}
// CONTEXT PROVIDER
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [, setError] = useState(null);
    const [clinicStatus, setClinicStatus] = useState(null);
    const setErrorState = (e) => {
        console.error('Error fetching user', e);
        setError('auth_error');
        setUser(null);
        setIsAuthenticated(false);
    };
    const fetchClinicStatus = async () => {
        const impersonatedClinic = sessionStorage.getItem('impersonatedClinic');
        const impersonatedClinicId = !!impersonatedClinic ? JSON.parse(impersonatedClinic)?.id : null;
        try {
            const status = await getClinicStatus(impersonatedClinicId);
            setClinicStatus(status);
        }
        catch (e) {
            setClinicStatus(null);
        }
    };
    // Check session status on mount and when needed
    const checkAuthStatus = async () => {
        try {
            setIsLoading(true);
            const response = await fetch('/api/v1/current-user/', {
                credentials: 'include', // Important for session cookies
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data);
                setIsAuthenticated(true);
                fetchClinicStatus();
                return data;
            }
            else {
                setUser(null);
                setIsAuthenticated(false);
                setClinicStatus(null);
                return null;
            }
        }
        catch (e) {
            setErrorState(e);
            setUser(null);
            setIsAuthenticated(false);
            setClinicStatus(null);
            return null;
        }
        finally {
            setIsLoading(false);
        }
    };
    // Check auth status on mount
    useEffect(() => {
        checkAuthStatus();
    }, []);
    const getClinicStatus = async (clinicId = null) => {
        try {
            const response = await fetch(`/api/v1/clinic-status/${!!clinicId ? `?clinic_id=${clinicId}` : ""}`, {
                headers: { Authorization: `Bearer ${user?.accessToken}` },
            });
            const data = await response.json();
            if (!response.ok)
                throw new Error(data?.error || 'Error checking clinic status');
            return data.status;
        }
        catch (e) {
            console.error('Clinic status error:', e);
            return 'rejected'; // fallback
        }
    };
    const signup = async ({ name, role, email, password }) => {
        try {
            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('csrftoken='))
                ?.split('=')[1];
            const response = await fetch('/api/v1/signup/', {
                method: 'POST',
                body: JSON.stringify({ name, role, email, password }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '', // Add CSRF token to headers
                },
                credentials: 'include', // Include cookies in the request
            });
            const data = await response.json();
            if (!response.ok) {
                if (response.status === 400 && data.error) {
                    const errorMessages = Object.entries(data.error)
                        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                        .join('\n');
                    return { success: false, message: errorMessages };
                }
                throw new Error(data?.error || 'Error signing up');
            }
            return { success: true, message: data.message, token: data.token, uid: data.uid };
        }
        catch (e) {
            console.error('Signup error:', e);
            return { success: false, message: e.message || 'Signup failed' };
        }
    };
    const submitClinicInfo = async (form, uid) => {
        try {
            console.log('Submitting clinic info with data:', { ...form, uid });
            // Get CSRF token from cookie
            const csrfToken = getCookie('csrftoken');
            // console.log('CSRF Token:', csrfToken);
            const response = await fetch('/api/v1/submit-clinic-info/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRFToken': csrfToken || '', // Add CSRF token to headers
                },
                credentials: 'include', // Include cookies in the request
                body: JSON.stringify({ ...form, uid }),
            });
            let data;
            try {
                data = await response.json();
            }
            catch (e) {
                console.error('Error parsing response:', e);
                throw new Error('Server returned an invalid response');
            }
            // console.log('Response from server:', data);
            if (!response.ok) {
                // If there are validation errors, include them in the error message
                if (data.errors) {
                    const errorMessages = Object.entries(data.errors)
                        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                        .join('; ');
                    throw new Error(errorMessages || data.message || 'Failed to submit clinic info');
                }
                // Handle 500 errors specifically
                if (response.status === 500) {
                    throw new Error('Server error occurred. Please try again later.');
                }
                throw new Error(data.message || data.detail || 'Failed to submit clinic info');
            }
            return { success: true, message: data.message };
        }
        catch (e) {
            console.error('Clinic submission error:', e);
            return { success: false, message: e.message || 'Submission failed' };
        }
    };
    const verifyEmail = async (token, uid) => {
        try {
            const res = await fetch(`/api/v1/verify-email?token=${token}&uid=${uid}`);
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.message || data?.error || 'Invalid token or user');
            }
            return { success: true, message: data.message };
        }
        catch (e) {
            console.error('Email verification error:', e);
            return { success: false, message: e.message || 'Verification failed' };
        }
    };
    const login = async ({ email, password }) => {
        try {
            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('csrftoken='))
                ?.split('=')[1];
            const response = await fetch('/api/v1/login/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include', // Important for session cookies
                body: JSON.stringify({ username: email, password }),
            });
            const data = await response.json();
            if (!response.ok) {
                const errorData = data;
                return {
                    message: errorData.detail ?? response.statusText ?? 'Login failed',
                    status: response.status,
                    ok: false,
                    url: response.url,
                    usernameError: errorData.username?.[0] ?? '',
                    passwordError: errorData.password?.[0] ?? '',
                };
            }
            // After successful login, fetch user data
            const userData = await checkAuthStatus();
            try {
                const { migrateGuestCartToUser } = await import('../utils/cartStorage');
                await migrateGuestCartToUser(true);
            }
            catch (err) {
                console.error('Failed to migrate guest cart:', err);
            }
            return {
                isSuccess: true,
                user: userData,
                token: 'session',
            };
        }
        catch (error) {
            console.error('Unexpected login error:', error);
            return {
                message: 'Unexpected error during login',
            };
        }
    };
    const logout = async () => {
        try {
            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('csrftoken='))
                ?.split('=')[1];
            await fetch('/api/v1/logout/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken || '',
                },
                credentials: 'include',
            });
        }
        catch (error) {
            console.error('Logout error:', error);
        }
        finally {
            setUser(null);
            setIsAuthenticated(false);
        }
    };
    const isClinicAuthorized = clinicStatus !== 'pendingClinicApproval' &&
        clinicStatus !== 'rejected' &&
        clinicStatus !== null;
    const isCheckoutAuthorized = isClinicAuthorized &&
        clinicStatus !== 'onHold';
    return (_jsx(AuthContext.Provider, { value: {
            isAuthenticated,
            user,
            isLoading,
            login,
            logout,
            signup,
            verifyEmail,
            submitClinicInfo,
            getClinicStatus,
            fetchClinicStatus,
            clinicStatus,
            isClinicAuthorized,
            isCheckoutAuthorized,
        }, children: children }));
};
// HOOK USED TO GRAB CONTEXT
export const useAuth = () => {
    const authContext = useContext(AuthContext);
    if (!authContext) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return authContext;
};
